import { useEffect, useRef, useState } from 'react';
import { loadAssetPrices } from '../../api/assetPricesClient';
import { loadFredSeries } from '../../api/fredClient';
import type { OHLCVPoint } from '../../api/types';
import {
    backendTicker,
    bucketPoints,
    clampStart,
    tiingoFrequency,
} from './tiingo';

export interface PriceData {
    points: OHLCVPoint[];
    loading: boolean;
    error: boolean;
}

const NO_POINTS: OHLCVPoint[] = [];

export function usePriceData(
    ticker: string | null,
    assetType: string | null,
    fetchRange: [Date, Date] | null,
    resolutionMinutes: number
): PriceData {
    const [points, setPoints] = useState<OHLCVPoint[]>(NO_POINTS);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const callId = useRef(0);
    const assetKey = ticker && assetType ? `${assetType}:${ticker}` : null;
    const lastAsset = useRef<string | null>(null);

    const startMs = fetchRange?.[0].getTime() ?? null;
    const endMs = fetchRange?.[1].getTime() ?? null;

    useEffect(() => {
        if (!ticker || !assetType || startMs === null || endMs === null) {
            setPoints(NO_POINTS);
            setLoading(false);
            return;
        }
        if (lastAsset.current !== assetKey) {
            lastAsset.current = assetKey;
            setPoints(NO_POINTS);
        }
        const id = ++callId.current;
        const controller = new AbortController();
        const { freq, minutes } = tiingoFrequency(resolutionMinutes);
        const symbol = backendTicker(ticker);
        const start = clampStart(ticker, new Date(startMs));
        const end = new Date(endMs);
        setLoading(true);
        setError(false);

        const request =
            assetType === 'macro'
                ? loadFredSeries(symbol, start, end)
                : loadAssetPrices(symbol, assetType, start, end, {
                      signal: controller.signal,
                      frequency: freq,
                      aggregate: freq === '1day' ? 'daily' : 'none',
                  });

        request
            .then(raw => {
                if (id !== callId.current) return;
                const sorted = [...raw].sort(
                    (a, b) => a.t.getTime() - b.t.getTime()
                );
                setPoints(bucketPoints(sorted, resolutionMinutes, minutes));
                setLoading(false);
            })
            .catch(err => {
                if (id !== callId.current || err?.name === 'AbortError') {
                    return;
                }
                console.error('asset prices failed:', err);
                setError(true);
                setLoading(false);
            });

        return () => controller.abort();
    }, [ticker, assetType, assetKey, startMs, endMs, resolutionMinutes]);

    return { points, loading, error };
}
